import React, { useCallback, useEffect, useState } from 'react';
import {
  BalanceReturn,
  LedgerEntryLine,
  TenureReturn,
} from '../clients/rust-api-client';
import { billingService } from '../clients/ApiClients';
import { Button } from '../common/Catalyst/button';
import { Divider } from '../common/Catalyst/divider';
import { Subheading } from '../common/Catalyst/heading';
import { Text } from '../common/Catalyst/text';
import LoadingSpinner from '../LoadingSpinner';
import ShowAlert from '../ShowAlert';
import { billingErrorMessage } from '../utils/billingHelpers';
import HomeBalanceCard from './HomeBalanceCard';
import HomeLedgerTable from './HomeLedgerTable';
import PostEntryDialog, { PostKind } from './PostEntryDialog';
import ReverseEntryDialog from './ReverseEntryDialog';

interface Props {
  hoaId: string;
  homeId: string;
  canPostCharge: boolean;
  canPostCredit: boolean;
  canReverse: boolean;
}

/**
 * A home's account: what it owes, and every entry that got it there.
 *
 * The balance and the ledger are separate reads, so both are refreshed
 * after any write — the cached balance is what the payer file bills
 * from, and a card disagreeing with the rows under it is the single
 * most alarming thing this screen could show.
 */
const HomeAccountPanel: React.FC<Props> = ({
  hoaId,
  homeId,
  canPostCharge,
  canPostCredit,
  canReverse,
}) => {
  const [balance, setBalance] = useState<BalanceReturn | null>(null);
  const [tenure, setTenure] = useState<TenureReturn | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [posting, setPosting] = useState<PostKind | null>(null);
  const [reversing, setReversing] = useState<LedgerEntryLine | null>(null);

  const loadBalance = useCallback(async () => {
    try {
      setLoading(true);
      const [b, t] = await Promise.all([
        billingService.getHomeBalance({ homeId }),
        billingService.listTenures({ homeId }),
      ]);
      setBalance(b.data);
      setTenure(t.data.tenures.find((x) => x.status === 'active') ?? null);
    } catch (err) {
      console.error('Error loading balance:', err);
      setError(billingErrorMessage(err, "Couldn't load this account."));
    } finally {
      setLoading(false);
    }
  }, [homeId]);

  useEffect(() => {
    void loadBalance();
  }, [loadBalance]);

  // Every write moves the balance as well as the ledger.
  const refresh = (): void => {
    void loadBalance();
    setReloadToken((t) => t + 1);
  };

  if (loading && !balance) return <LoadingSpinner />;

  return (
    <div>
      {error && (
        <div className="mb-4">
          <ShowAlert
            alertType="error"
            alertTitle="Couldn't load this account"
            alertMessage={error}
            isVisible={!!error}
            onClose={() => setError(null)}
            timeout={10000}
            isInline
          />
        </div>
      )}

      {balance && <HomeBalanceCard balance={balance} tenure={tenure} />}

      <Divider className="my-8" />

      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Subheading>Ledger</Subheading>
          <Text className="mt-1">
            Every charge, credit and payment on this home. Nothing here is
            edited or deleted — a correction is a new entry against the one it
            corrects, and both stay visible.
          </Text>
        </div>
        <div className="flex gap-2">
          {canPostCharge && (
            <Button color="sky" onClick={() => setPosting('charge')}>
              Post charge
            </Button>
          )}
          {canPostCredit && (
            <Button outline onClick={() => setPosting('credit')}>
              Post credit
            </Button>
          )}
        </div>
      </div>

      <HomeLedgerTable
        hoaId={hoaId}
        homeId={homeId}
        canReverse={canReverse}
        onReverse={setReversing}
        reloadToken={reloadToken}
      />

      <PostEntryDialog
        isOpen={posting !== null}
        kind={posting ?? 'charge'}
        homeId={homeId}
        onClose={() => setPosting(null)}
        onPosted={refresh}
      />
      <ReverseEntryDialog
        isOpen={reversing !== null}
        entry={reversing}
        onClose={() => setReversing(null)}
        onReversed={refresh}
      />
    </div>
  );
};

export default HomeAccountPanel;
